import React, { useCallback, useEffect, useMemo } from 'react';
import { Box, getFullnameAbbr, OptionKey, SelectDropdown, Text } from '@common-fe/common-fe';
import { capitalize } from 'lodash';

import regexp from '@/common/regexp';
import ROUTES from '@/common/routes';
import { useBreadcrumbs } from '@/modules/core/hooks';

import { useDependents } from './SpendTracker/hooks';
import { DeductibleUser } from './SpendTracker/hooks/useDependents';
import SpendTracker from './SpendTracker/SpendTracker';
import useDeductiblesStore from './stores/useDeductibles.store';

const formatRelation = (type?: string) => capitalize(type)
  ?.replace(regexp.DASH_SYMBOL, ' ');

interface Props {
  userId?: string;
  employeeUser: DeductibleUser;
  healthPlanYearId?: string;
}

const Deductibles: React.FC<Props> = ({ userId, employeeUser, healthPlanYearId }) => {
  const handleSetState = useDeductiblesStore((state) => state.setState);
  const selectedUserId = useDeductiblesStore((state) => state.state.userId);

  const users = useDependents({
    userId, employeeUser,
  });

  const currentUser = useMemo(() => users?.find((user) => user.dependentId === selectedUserId), [selectedUserId, users]);
  const abbr = useMemo(() => getFullnameAbbr(currentUser?.fullName || ''), [currentUser]);
  const usersOptions = useMemo(() => {
    if (!users?.length) return [];
    return users?.map((user) => ({
      key: user.dependentId,
      value: user.dependentId,
      title: `${user.fullName} (${formatRelation(user?.relationshipType)})`,
    }));
  }, [users]);

  const handleSetUserId = useCallback((userId: OptionKey) => {
    handleSetState({ userId: userId as string });
  }
  , [handleSetState]);

  useEffect(() => {
    if (userId) {
      handleSetState({ userId });
    }
  }, [userId, handleSetState]);

  useBreadcrumbs([{
    route: ROUTES.EMPLOYEES,
    title: `Deductible ${currentUser?.fullName}`,
  }]);

  return (
    <Box direction="column" fill>
      <Box
        pad={{ vertical: 'xxs', horizontal: 'xs' }}
        direction="row"
        round="l"
        align="center"
        justify='between'
      >
        <Box direction="row" align="center">
          <Box
            width="40px"
            height="40px"
            align="center"
            round="50%"
            justify="center"
            background="subgroup"
          >
            <Text color="iconOnColor" weight={700}>
              {abbr}
            </Text>
          </Box>
          <Text
            margin={{ left: 'xs' }}
            size="3xl"
            color="textBody"
            weight={700}
          >
            {`${currentUser?.fullName} (${formatRelation(currentUser?.relationshipType)})`}
          </Text>
        </Box>
        {
          users?.length ? (
            <Box width="200px" margin={{ left: 'spacing8' }}>
              <SelectDropdown
                id="deductible-users"
                testId="deductible-users"
                name="deductibleUsers"
                singleMode
                activeTitle
                options={usersOptions}
                value={selectedUserId}
                onChange={handleSetUserId}
              />
            </Box>
          ) : null
        }
      </Box>
      <SpendTracker
        isIndividual
        employeeUser={employeeUser}
        defaultHealthPlanYearId={healthPlanYearId}
      />
    </Box>
  );
};

export default Deductibles;
